<template>
  <section ref="rootRef" class="single-content-rail">
    <TG4Image
      :image="image"
      aspect="banner"
      :alt="title"
      class="banner-image"
      :id="itemId(0)"
      tabindex="0"
      v-focus
      @up="handleUp"
      @down="handleDown"
      @left="handleLeftFallback"
      v-press="handlePress"
    />

    <div class="overlay" :style="textStyle">
      <h3 class="rail-title" v-if="title">{{ title }}</h3>
      <p class="rail-subtitle" v-if="subtitle">{{ subtitle }}</p>
    </div>

    <TG4PlayIcon class="play-icon" />
  </section>
</template>

<script setup lang="ts">


import { ref, computed, watch, onMounted, getCurrentInstance } from 'vue';
import { RailResponse } from '@/types/cms';
import { Language } from '@/enums/language';
import { useStore } from '@/composables/useStore';
import { useRailNavigation } from '@/composables/useRailNavigation';
import { useCMSItemNavigation } from '@/composables/useCMSItemNavigation';
import TG4Image from '@/components/TG4Image.vue';
import TG4PlayIcon from '@/components/TG4PlayIcon.vue';

const props = defineProps<{
  rail: RailResponse;
  railIndex: number;
  railDidNavigateDown?: (railIndex: number) => void;
}>();

const { itemId, handleUp, handleDown, handleLeftFallback } = useRailNavigation(props);
const { openItem } = useCMSItemNavigation();

const { useGetter } = useStore();
const currentLanguage = useGetter<string>('currentLanguage');

const localised = (en: string | null, ga: string | null) => {
  const value = currentLanguage.value === Language.Irish ? ga : en;
  return value ?? en ?? '';
};

const title = computed(() => localised(props.rail.titleEn, props.rail.titleGa));
const subtitle = computed(() => localised(props.rail.subtitleEn, props.rail.subtitleGa));

const item = computed(() => props.rail.items[0] ?? null);

const image = computed(() => {
  const value = item.value;
  if (!value) return null;
  if (value.type === 'SERIES') return value.series.mainImage;
  if (value.type === 'VIDEO') return value.video.image;
  return null;
});

const textStyle = computed(() => ({
  color: props.rail.textColor ?? undefined,
}));

const handlePress = () => {
  if (item.value) openItem(item.value);
};

const rootRef = ref<HTMLElement | null>(null);
const lazyLoad = getCurrentInstance()?.proxy?.$lazyLoad;

const scanForLazyImages = () => {
  setTimeout(() => {
    if (rootRef.value) lazyLoad?.(rootRef.value);
  }, 200);
};

onMounted(scanForLazyImages);

</script>

<style scoped lang="scss">
.single-content-rail {
  position: relative;
  display: block;
  margin-bottom: 40px;
}

.banner-image {
  outline: 0;
  box-sizing: border-box;

  border: $focus-border-width solid transparent;

  &:focus {
    border-color: $color-pink;
  }
}

.overlay {
  position: absolute;
  top: 0;
  bottom: 0;
  left: 0;
  right: 50%;
  display: flex;
  flex-direction: column;
  justify-content: center;
  padding: 0 60px;
  text-align: left;
  @include text-white;
}

.rail-title {
  margin: 0;
  @include text-title;
}

.rail-subtitle {
  margin: 10px 0 0 0;
  @include text-rail-subtitle;
}

.play-icon {
  position: absolute;
  left: $focus-border-width;
  bottom: $focus-border-width;
  width: 120px;
  height: 120px;
}
</style>
